<template>
    <div
        :class="classTextField"
        class="text-field"
        @click="focusInputElement"
    >
        <label
            v-if="label"
            :for="id"
            class="label"
        >
            {{ label }}{{ required ? '*' : '' }}

            <slot name="tooltip"></slot>
        </label>

        <div class="input-container">
            <input
                v-if="lazy"
                :id
                ref="inputRef"
                v-model.lazy="val"
                :class="classInput"
                :max
                :maxlength
                :min
                :name="id"
                :placeholder="placeholderWithRequiredIndicator"
                :readonly
                :type
                class="input"
            />

            <input
                v-else
                :id
                ref="inputRef"
                v-model="val"
                :class="classInput"
                :max
                :maxlength
                :min
                :name="id"
                :placeholder="placeholderWithRequiredIndicator"
                :readonly
                :type
                class="input"
            />

            <div
                v-if="maxlength"
                class="maxlength-indicator"
            >
                {{ val?.length || 0 }} / {{ maxlength }}
            </div>
        </div>

        <FieldError
            v-if="error"
            :error
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'

import FieldError from '@/components/ui/inputs/ThFieldError.vue'
import { useFocus } from '@vueuse/core'

defineSlots<{
    'tooltip': () => any
}>()

interface Props {
    type?: 'text' | 'number' | 'password' | 'url' | 'email' | 'tel'
    min?: number
    max?: number
    maxlength?: number
    label?: string
    placeholder?: string
    required?: boolean
    error?: string
    readonly?: boolean
    id: string
    lazy?: boolean
    focus?: boolean
    // I shot the serif
    serifFont?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    type: 'text',
    min: undefined,
    max: undefined,
    maxlength: undefined,
    label: undefined,
    placeholder: undefined,
    required: undefined,
    error: undefined,
    readonly: undefined,
    lazy: undefined,
    focus: false,
})

const val = defineModel<string | null>()

const placeholderWithRequiredIndicator = computed(() =>
    !props.label && props.required && props.placeholder
        ? `${props.placeholder}*`
        : props.placeholder
)

const classTextField = computed(() => ({
    error: props.error,
}))

const classInput = computed(() => ({
    serifFont: props.serifFont,
}))

const inputRef = ref<HTMLElement | null>(null)
useFocus(inputRef, { initialValue: props.focus })

const focusInputElement = () => {
    inputRef.value?.focus()
}
</script>

<style lang="scss" scoped>
.text-field {
    width: 100%;
    font-family: $effra-font-stack;
    font-weight: 400;
    line-height: 1.25rem;

    .label {
        display: flex;
        align-items: center;
        font-size: 0.75rem;
        letter-spacing: 0.05em;
        text-transform: uppercase;
        color: $slate;
        margin-bottom: 5px;
        line-height: 20px;

        :deep(.tooltip-trigger) {
            &::after {
                margin-left: 5px;
                font-size: 1rem;
                height: 16px;
                width: 16px;
                line-height: 1em;
            }
        }
    }

    .input-container {
        display: flex;
        align-items: center;
        border: 1px solid rgba($slate, 0.5);
        border-radius: 2px;
        width: 100%;
        height: 50px;

        &:has(.input:focus) {
            outline: 2px solid $slate;
        }

        .input {
            width: 100%;
            padding: 15px 10px;
            font-size: 1rem;
            line-height: 1.25rem;
            font-family: $effra-font-stack;
            font-weight: 400;
            letter-spacing: 0.05em;

            &.serifFont {
                font-family: $sectra-font-stack;
                letter-spacing: unset;
            }

            &:focus {
                outline: none;
            }

            &::placeholder {
                color: $grey-400;
            }

            &:read-only {
                color: $grey-500;
            }
        }

        .maxlength-indicator {
            background-color: $stone;
            margin: 0 10px;
            flex-shrink: 0;
            padding: 4px 8px;
            border-radius: 8px;
        }
    }

    &.error {
        .label {
            color: rgba($red, 0.7);
        }

        .input {
            color: $red;
            border-color: rgba($red, 0.5);
        }
    }
}
</style>
